import { useEffect, useRef, useCallback } from 'react';
import { useAuthStore } from '@/stores/authStore';

interface UseInactivityLogoutOptions {
  timeout?: number; // in milliseconds
  enabled?: boolean;
  onLogout?: () => void;
}

export const useInactivityLogout = ({
  timeout = 30 * 60 * 1000, // 30 minutes default
  enabled = true,
  onLogout
}: UseInactivityLogoutOptions = {}) => {
  const { logout } = useAuthStore();
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef(Date.now());
  const isActiveRef = useRef(true);
  const heartbeatRef = useRef<NodeJS.Timeout | null>(null);

  // Reset the timer
  const resetTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    if (enabled) {
      timerRef.current = setTimeout(async () => {
        try {
          // Check if user has been inactive for the full timeout period
          const timeSinceLastActivity = Date.now() - lastActivityRef.current;
          if (timeSinceLastActivity < timeout || !isActiveRef.current) {
            // User has been active or page is not active, reset timer
            resetTimer();
            return;
          }

          console.log('🔄 Auto-logout due to inactivity');
          
          // Call custom logout handler if provided
          onLogout?.();
          
          // Clear auth data
          logout();
          
          // Redirect to login page
          window.location.href = '/login';
        } catch (error) {
          console.error('[INACTIVITY] Error during inactivity check:', error);
        }
      }, timeout);
    }
  }, [enabled, timeout, onLogout, logout]);

  // Track user activity
  const trackActivity = useCallback(() => {
    if (isActiveRef.current) {
      lastActivityRef.current = Date.now();
      resetTimer();
    }
  }, [resetTimer]);

  // Handle page visibility changes
  const handleVisibilityChange = useCallback(() => {
    if (document.hidden) {
      // Page is hidden, don't reset timer but track when user was last active
      lastActivityRef.current = Date.now();
    } else {
      // Page is visible again, reset timer
      isActiveRef.current = true;
      lastActivityRef.current = Date.now();
      resetTimer();
    }
  }, [resetTimer]);

  // Extend session manually (useful for form submissions, etc.)
  const extendSession = useCallback(() => {
    lastActivityRef.current = Date.now();
    resetTimer();
  }, [resetTimer]);

  // Start heartbeat to keep session alive
  const startHeartbeat = useCallback(() => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
    }
    
    // Emit activity every 5 minutes to keep session alive
    heartbeatRef.current = setInterval(() => {
      if (isActiveRef.current && !document.hidden) {
        lastActivityRef.current = Date.now();
        window.dispatchEvent(new Event('app-activity'));
      }
    }, 5 * 60 * 1000); // 5 minutes
  }, []);

  // Stop heartbeat
  const stopHeartbeat = useCallback(() => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;

    // Set up event listeners for user activity
    const events = [
      'mousedown',
      'mousemove', 
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'keydown',
      'wheel',
      'keyup',
      'mouseup',
      'focus',
      'blur',
      'input',
      'change',
      'submit',
      'resize',
      'orientationchange',
      // Custom app activity event fired from API client and other subsystems
      'app-activity'
    ];

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, trackActivity, { passive: true });
    });

    // Add visibility change listener
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Add window focus/blur events
    window.addEventListener('focus', trackActivity);
    window.addEventListener('blur', () => {
      // Window lost focus, but keeping timer active
    });

    // Initialize timer and heartbeat
    resetTimer();
    startHeartbeat();

    // Cleanup function
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, trackActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', trackActivity);
      window.removeEventListener('blur', () => {});
      
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      stopHeartbeat();
    };
  }, [enabled, trackActivity, handleVisibilityChange, resetTimer, startHeartbeat, stopHeartbeat]);

  return {
    extendSession,
    resetTimer: trackActivity
  };
};
